import { useState } from 'preact/hooks';
import { partyBalance } from '@khata/core';
import { DateInput, Field, MoneyInput, Select, TextInput } from '../components/index.js';
import { fromApi, isErr, money, useFields, useSave } from '../lib/form.js';
import { t } from '../lib/i18n.js';
import { createParty, deleteParty, master, state, today, updateParty } from '../lib/khata.js';
import { go } from '../lib/router.js';
import { FormErrorLine, SaveButton, Sheet } from './Sheet.js';

export function PartySheet(props: { id?: string; type?: 'customer' | 'vendor' }) {
  const m = master.value;
  const existing = props.id ? m.parties.get(props.id) : undefined;
  const [f, set] = useFields({
    name: existing?.name ?? '',
    type: existing?.type ?? props.type ?? 'customer',
    phone: existing?.phone ?? '',
    note: existing?.note ?? '',
    openAmount: '',
    openDir: 'due',
    openDate: today.value,
  });
  const [error, busy, save] = useSave();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const isC = f.type === 'customer';

  const onSave = () =>
    save(async () => {
      if (!f.name.trim()) return { message: t('party_needsName'), field: 'name' };
      const input = { name: f.name, type: f.type, phone: f.phone, note: f.note };
      if (existing) {
        const r = await updateParty(existing.id, { ...input, active: true });
        return r.ok ? t('party_saved', { name: r.value.name }) : fromApi(r.error);
      }
      let opening: { date: string; amount: number } | undefined;
      if (f.openAmount.trim() !== '' && f.openAmount.trim() !== '0') {
        const a = money(f.openAmount, 'openAmount', { min: 1 });
        if (isErr(a)) return a;
        opening = { date: f.openDate, amount: f.openDir === 'due' ? a : -a };
      }
      const r = await createParty(input, opening);
      if (!r.ok) return fromApi(r.error);
      go('party', r.value.party.id);
      return t('party_added', { name: r.value.party.name });
    });

  const onDelete = () =>
    save(async () => {
      if (!existing) return null;
      const r = await deleteParty(existing.id);
      if (!r.ok) return fromApi(r.error);
      go('parties');
      return r.value.result === 'deleted'
        ? t('party_deleted')
        : t('party_archived', { name: existing.name });
    });

  return (
    <Sheet
      title={existing ? t('sheet_editParty') : t('sheet_newParty')}
      footer={<SaveButton busy={busy} onClick={onSave} />}
    >
      <Field label={t('item_name')}>
        <TextInput
          value={f.name}
          onInput={(v) => set('name', v)}
          invalid={error?.field === 'name'}
          autoFocus={!existing}
        />
      </Field>
      <div class="grid-2">
        <Field label={t('party_type')}>
          <Select
            value={f.type}
            onChange={(v) => set('type', v)}
            disabled={!!existing && partyBalance(state.value, existing) !== 0}
            invalid={error?.field === 'type'}
          >
            <option value="customer">{t('customer')}</option>
            <option value="vendor">{t('vendor')}</option>
          </Select>
        </Field>
        <Field label={t('party_phone')}>
          <TextInput value={f.phone} onInput={(v) => set('phone', v)} inputMode="tel" />
        </Field>
      </div>
      <Field label={t('party_address')}>
        <TextInput value={f.note} onInput={(v) => set('note', v)} />
      </Field>
      {!existing && (
        <>
          <h6 class="section" style={{ marginTop: 8 }}>
            {t('party_openingHead')}
          </h6>
          <div class="grid-3">
            <Field label={t('amountTaka')}>
              <MoneyInput
                value={f.openAmount}
                onInput={(v) => set('openAmount', v)}
                invalid={error?.field === 'openAmount'}
              />
            </Field>
            <Field label={t('party_whichWay')}>
              <Select value={f.openDir} onChange={(v) => set('openDir', v)}>
                <option value="due">{isC ? t('party_theyOwe') : t('party_youOweThem')}</option>
                <option value="advance">
                  {isC ? t('party_advanceFrom') : t('party_advanceTo')}
                </option>
              </Select>
            </Field>
            <Field label={t('item_asOf')}>
              <DateInput value={f.openDate} onInput={(v) => set('openDate', v)} />
            </Field>
          </div>
        </>
      )}
      <FormErrorLine error={error} />
      {existing && (
        <div class="actions" style={{ marginBottom: 0 }}>
          {!confirmDelete ? (
            <button
              type="button"
              class="btn btn-ghost btn-danger"
              onClick={() => setConfirmDelete(true)}
            >
              {t('party_delete')}
            </button>
          ) : (
            <button
              type="button"
              class="btn btn-secondary btn-danger"
              onClick={onDelete}
              disabled={busy}
            >
              {t('confirmDelete')}
            </button>
          )}
          {confirmDelete && <span class="help">{t('party_deleteHelp')}</span>}
        </div>
      )}
    </Sheet>
  );
}
